export const services = [
  {
    number: "01",
    title: "Technology Consultancy",
    audience: "For established companies.",
    description:
      "Architecture, modernisation and senior engineering support for teams that need to move fast without breaking things.",
    items: [
      "Architecture",
      "Cloud transformation",
      "Java/Spring modernisation",
      "Microservices",
      "Kafka & event-driven systems",
      "API integration",
      "Kubernetes",
      "DevOps",
      "Performance",
      "Technical leadership",
    ],
  },
  {
    number: "02",
    title: "Product & SaaS Development",
    audience: "For founders and businesses.",
    description: "Have an idea? We'll turn it into a working product.",
    items: [
      "MVP development",
      "Web applications",
      "Mobile applications",
      "SaaS",
      "APIs",
      "Payments",
      "Authentication",
      "Admin platforms",
      "Cloud deployment",
    ],
  },
  {
    number: "03",
    title: "AI & Automation",
    audience: "For companies wanting to use AI practically.",
    description:
      "Practical AI that fits into how your business already works.",
    items: [
      "AI assistants",
      "RAG",
      "Agentic AI",
      "Business workflow automation",
      "Document intelligence",
      "Internal knowledge systems",
      "AI integrations",
    ],
  },
  {
    number: "04",
    title: "Support & Augmentation",
    audience: "For teams that need ongoing senior support.",
    description:
      "Extend your team with senior engineering on retainer. Not just a one-off project, but someone senior on call when it matters.",
    items: [
      "Fractional / part-time senior engineering",
      "Team augmentation",
      "Ongoing maintenance",
      "Monitoring & alerting",
      "Production incident support",
      "Code & security reviews",
      "Technical due diligence",
    ],
  },
];

// Big card packs on the homepage, grouped as Foundation and Cloud and AI.
// status "ready": built and tested, fixed scope and price.
// status "scoped": Mantle does this work, but it is NOT a pre-built accelerator, so
// the copy must not claim ready made code or fixed packages. Change a pack to
// "ready" only once it is built. No prices are shown on purpose.
export const solutionPacks = [
  {
    slug: "identity-security",
    group: "foundation",
    status: "scoped",
    title: "Identity and Security",
    problem: "Your applications and users need to be secure from day one.",
    outcome:
      "Login, access control and session security built into your Spring Boot and React applications, connected to the identity provider you already use.",
    includes: [
      "OAuth2 and OpenID Connect, single sign on and MFA",
      "Role based and attribute based access control",
      "Microsoft, Google, GitHub, Keycloak, Okta or Cognito",
      "API keys and sessions for services and partners",
    ],
  },
  {
    slug: "api-integration",
    group: "foundation",
    status: "scoped",
    title: "API and Integration",
    problem: "Your systems do not talk to each other, or do so unreliably.",
    outcome:
      "Clean, documented APIs and integrations between your systems, built to cope with slow or failing partners.",
    includes: [
      "REST and GraphQL APIs documented with OpenAPI",
      "Webhooks and third party integrations",
      "Gateway, versioning and rate limiting",
      "Retries and circuit breakers so one failure does not spread",
    ],
  },
  {
    slug: "messaging-resilience",
    group: "foundation",
    status: "ready",
    title: "Messaging and Resilience",
    problem:
      "Messages get lost, duplicated or stuck when something fails.",
    outcome:
      "Events are saved together with the business change, duplicates are harmless, and bad messages are parked instead of blocking the queue.",
    includes: [
      "Transactional outbox and idempotent consumers",
      "Retries, dead letter handling and circuit breakers",
      "Failure tests against a real database and broker",
      "Alerts, runbook and team handover",
    ],
    engagement: "Around four weeks, fixed price",
  },
  {
    slug: "email-notifications",
    group: "foundation",
    status: "scoped",
    title: "Email and Notifications",
    problem:
      "Customers and staff miss important messages, or you send them by hand.",
    outcome:
      "Reliable email, SMS and chat notifications from your application, with templates you can change and delivery you can track.",
    includes: [
      "Email, SMS, push and WhatsApp",
      "Slack and Microsoft Teams alerts for your team",
      "Reusable templates, scheduled and bulk sending",
      "Retries and delivery tracking",
    ],
  },
  {
    slug: "documents-files",
    group: "foundation",
    status: "scoped",
    title: "Documents and Files",
    problem:
      "Your team spends hours handling spreadsheets, PDFs and documents by hand.",
    outcome:
      "Automatic processing of the files your business runs on, with safe handling built in.",
    includes: [
      "PDF, Excel, CSV and Word reading and generation",
      "OCR for scanned documents and images",
      "Virus scanning and encryption for uploads",
      "Runs in your own cloud account",
    ],
  },
  {
    slug: "reporting-dashboards",
    group: "foundation",
    status: "scoped",
    title: "Reporting and Dashboards",
    problem:
      "Getting numbers out of your systems takes manual work, or nobody trusts them.",
    outcome:
      "Reports and dashboards that update themselves, delivered where your team already works.",
    includes: [
      "Scheduled PDF and Excel reports",
      "Live dashboards in Grafana or Power BI",
      "One agreed set of figures from your data",
      "Access controlled by role",
    ],
  },
  {
    slug: "aws-readiness",
    group: "cloud-ai",
    status: "ready",
    title: "AWS Readiness Assessment",
    problem: "You need to move to AWS but do not know where to start.",
    outcome:
      "A scored readiness report, a recommended approach for every application, and a wave plan that says what moves first.",
    includes: [
      "One day discovery workshop with your team",
      "Gaps to close first, with actions",
      "Strategy for each application, and why",
      "Optional secure AWS account baseline",
    ],
    engagement: "Around two weeks, fixed price",
  },
  {
    slug: "ai-document-processing",
    group: "cloud-ai",
    status: "ready",
    title: "AI Document Processing",
    problem: "Your team retypes invoices and forms by hand.",
    outcome:
      "Documents are read automatically and checked in code. Anything that does not add up is flagged for a person to review.",
    includes: [
      "Secure API and a review screen for your team",
      "Totals, dates and currency verified automatically",
      "Runs in your own cloud account",
      "Accuracy report measured on your own documents",
    ],
    engagement: "Around four weeks, fixed price",
  },
  {
    slug: "ai-assistants",
    group: "cloud-ai",
    status: "scoped",
    title: "AI Assistants and Chatbots",
    problem:
      "Your team loses hours searching for answers, or customers wait for simple replies.",
    outcome:
      "An assistant that answers from your own documents and hands over to a person when it is unsure.",
    includes: [
      "Customer service and internal helpdesk assistants",
      "Answers grounded in your own content",
      "Guardrails, monitoring and human handover",
      "Private deployment in your cloud account",
    ],
  },
];

// Smaller tiles for Platform and data work. Same rule as above: these are scoped
// engagements, not pre-built accelerators.
export const moreSolutions = [
  {
    title: "Microservices and Modernisation",
    summary: "Break up or modernise a monolith safely.",
    covers: "Service boundaries, gateways and configuration, distributed tracing, event driven design, caching.",
  },
  {
    title: "Kubernetes and Platform",
    summary: "Run your services reliably.",
    covers: "Helm, ingress, autoscaling, rolling and blue green releases, secrets management.",
  },
  {
    title: "CI/CD and Infrastructure as Code",
    summary: "Ship safely and often.",
    covers: "Build and deploy pipelines, Docker, Terraform, quality gates.",
  },
  {
    title: "Database Migration",
    summary: "Move databases without the drama.",
    covers: "Flyway or Liquibase, data validation, rollback plans, low downtime cutovers.",
  },
  {
    title: "Observability",
    summary: "Know what is happening in production.",
    covers: "Metrics, logs, traces, dashboards and alerts.",
  },
];

export const audiences = [
  {
    segment: "Founders & Startups",
    from: "From idea to product",
    description:
      "We help founders build the right first product and avoid spending budget on the wrong bets, so you reach real users faster.",
  },
  {
    segment: "SMEs",
    from: "From uncertainty to control",
    description:
      "We help growing businesses take back control of delivery, modernise ageing systems, and turn technology spend into results.",
  },
  {
    segment: "Financial Services",
    from: "From legacy to leverage",
    description:
      "We help financial services companies modernise Java/Spring platforms and event-driven systems without disrupting live operations.",
  },
];

export const faqs = [
  {
    question: "What does Mantle Tech Solutions do?",
    answer:
      "We're a software engineering consultancy. We design, build and modernise custom software, products and AI systems for financial services companies, SMEs and founders, using Java, Spring Boot, React, Kafka, Kubernetes and cloud platforms.",
  },
  {
    question: "How do projects start?",
    answer:
      "Every engagement starts with a short discovery call to understand the real problem. From there we scope the work and agree what \"done\" looks like before any code is written.",
  },
  {
    question: "Do you work with regulated and financial services firms?",
    answer:
      "Yes. Experience across financial services and distributed, high-reliability systems informs how we approach architecture, security and delivery on every engagement.",
  },
  {
    question: "Who owns the code and the data?",
    answer:
      "You do. Anything built for you is yours, including source code, infrastructure configuration and documentation.",
  },
  {
    question: "Do you work with small teams and early-stage founders too?",
    answer:
      "Yes. Engagements range from a single founder validating an MVP to established teams modernising a live platform.",
  },
];

// Real client case studies go here, only with the client's written permission.
// Kept empty rather than filled with placeholder or made up work. While it is
// empty the Case Studies section is not shown at all (see Home.js).
export const caseStudies = [];

export const projects = [
  {
    title: "CineHub: Movie Discovery App",
    subtitle: "Explore, search, and discover your next favorite movie",
    description:
      "CineHub is a movie discovery web application that lets users search for movies, view detailed information, watch trailers, and see ratings. Powered by React and TypeScript, it provides a clean, responsive UI with seamless navigation.",
    image: "/movieapp.gif",
    link: "https://findmoviestowatch.netlify.app/",
    tags: ["Web App", "React", "TypeScript"],
  },
  {
    title: "Admin Dashboard: React Application",
    subtitle: "A modern admin panel for data and user management",
    description:
      "Admin Dashboard is a React-based web application designed for managing data, users, and analytics efficiently. It features interactive charts, real-time updates, and a responsive UI for a seamless experience. Built with React, TypeScript, and modern UI frameworks for scalability and performance.",
    image: "/admindashboard.gif",
    link: "https://admindashboardmgt.netlify.app/",
    tags: ["Admin Platform", "React", "Data & Analytics"],
  },
  {
    title: "DelishEats: Food Ordering App",
    subtitle: "Order your favorite meals with ease",
    description:
      "DelishEats is a modern food ordering web application that allows users to browse a variety of dishes, add items to their cart, and complete orders seamlessly. Built with React, TypeScript, and styled for a smooth user experience.",
    image: "/foodorder.gif",
    link: "#", // Update the link when it's ready
    tags: ["Web App", "E-Commerce", "React"],
  },
  {
    title: "StockPulse: Real-Time & Historical Market Insights",
    subtitle: "Live stock tracking with 20+ years of historical data",
    description:
      "StockPulse is a powerful stock market analysis tool that provides real-time and historical stock data using the Alpha Vantage API. Get detailed OHLCV (Open, High, Low, Close, Volume) data, including pre-market and post-market hours. Perfect for traders and investors looking to analyze market trends with interactive charts and financial insights.",
    image: "/stock.gif",
    link: "#", // Update the link when it's ready
    tags: ["Fintech", "Data & Analytics", "API Integration"],
  },
  {
    title: "My Product Store",
    subtitle: "A Modern E-Commerce Platform",
    description:
      "My Product Store is a feature-rich e-commerce platform designed to provide a seamless shopping experience. Built with React, Node.js, and MongoDB, it allows users to browse products, add items to their cart, and securely complete purchases. With a sleek UI and robust backend, it ensures fast performance and scalability.",
    image: "/productstore.gif",
    link: "#", // Update the link when it's ready
    tags: ["SaaS", "E-Commerce", "Payments"],
  },
  {
    title: "InvestWise: Investment Calculator",
    subtitle: "Plan and forecast your investments with ease",
    description:
      "InvestWise is a user-friendly investment calculator that helps users project their returns based on initial investment, interest rates, and time duration. Built with React and TypeScript, it offers real-time calculations and a sleek, intuitive interface.",
    image: "/investCal.gif",
    link: "https://calculateintrestapp.netlify.app/",
    tags: ["Fintech", "React", "TypeScript"],
  },
];

export const TRUSTPILOT_PROFILE_URL =
  "https://www.trustpilot.com/review/mantletechsolutions.com";
export const TRUSTPILOT_REVIEW_URL =
  "https://www.trustpilot.com/evaluate/mantletechsolutions.com";

export const testimonials = [
  {
    quote:
      "Working with Mantle Tech Solutions was an absolute pleasure! They took our project to the next level, and their attention to detail was exceptional. I would highly recommend them to anyone looking for quality work delivered on time.",
    image: "https://randomuser.me/api/portraits/men/1.jpg",
    name: "Jesse Hicks",
    company: "CineVibe Studios",
  },
  {
    quote:
      "The professionalism and expertise demonstrated by the Mantle Tech Solutions team exceeded all of our expectations. They truly understand the intricacies of both design and functionality. We've seen a significant increase in productivity since implementing their solutions.",
    image: "https://randomuser.me/api/portraits/men/94.jpg",
    name: "Ruben Alvarez",
    company: "StockWise Investment",
  },
  {
    quote:
      "The project was completed ahead of schedule with outstanding quality. Mantle Tech Solutions' ability to quickly understand our needs and deliver exactly what we envisioned made all the difference. Truly a game-changer for our business.",
    image: "https://randomuser.me/api/portraits/men/2.jpg",
    name: "Jesse Hicks",
    company: "QuantumTrase Solutions",
  },
  {
    quote:
      "From start to finish, Mantle Tech Solutions was incredibly collaborative and flexible. They were always available to address our concerns and provided clear communication throughout the project. I can't recommend them enough!",
    image: "https://randomuser.me/api/portraits/men/3.jpg",
    name: "Ruben Alvarez",
    company: "PrimeCart Stores",
  },
];

// Segment-specific landing pages for outreach/ad campaigns. Slugs are used
// in routes (/for/:slug) so each channel/segment combo can carry its own
// UTM-tagged link back to a focused page instead of the general homepage.
export const campaigns = {
  "founders-startups": {
    segment: "Founders & Startups",
    headline: "From idea to a working product, without wasting your runway.",
    subheadline:
      "We help founders build the right first product and avoid spending budget on the wrong bets, so you reach real users faster.",
    painPoints: [
      "Unsure what to build first, or scared of over-building before you've validated anything",
      "Quotes from agencies that are way over budget, or freelancers who disappear mid-project",
      "You need senior engineering judgement, not just someone who can type code",
    ],
    valueProps: services[1].items,
    valuePropsTitle: "Product & SaaS Development",
  },
  smes: {
    segment: "SMEs",
    headline: "From uncertainty to control over your technology.",
    subheadline:
      "We help growing businesses take back control of delivery, modernise ageing systems, and turn technology spend into results.",
    painPoints: [
      "Delivery has slowed down and nobody can explain exactly why",
      "Legacy systems are getting riskier and harder to change",
      "You need senior engineering support without a full-time hire",
    ],
    valueProps: [...services[0].items.slice(0, 6), ...services[3].items.slice(0, 3)],
    valuePropsTitle: "Consultancy & Ongoing Support",
  },
  "financial-services": {
    segment: "Financial Services",
    headline: "From legacy to leverage: modernise without disrupting live operations.",
    subheadline:
      "We help financial services companies modernise Java/Spring platforms and event-driven systems without disrupting live operations.",
    painPoints: [
      "Legacy Java/Spring platforms that are risky to change and hard to hire for",
      "Event-driven systems and integrations that need senior, careful hands",
      "Regulatory and reliability requirements that most agencies aren't built for",
    ],
    valueProps: services[0].items,
    valuePropsTitle: "Technology Consultancy",
  },
};

export const skills = [
  "JavaScript",
  "React",
  "Next.js",
  "Node",
  "GraphQL",
  "Material UI",
  "TailwindCSS",
  "Flowbite",
  "Java",
  "Spring Boot",
  "AWS",
  "Docker",
  "Kubernetes",
  "MongoDB",
  "Jira",
  "Git",
  "Bitbucket",
  "GitLab",
  "Kafka",
  "Sonar",
];
